import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import Svg, { Circle } from "react-native-svg";
import { useAtom } from "jotai";
import { useMutation, useQuery } from "@apollo/client/react";
import { StackScreenProps } from "@react-navigation/stack";
import ScreenCard from "../ScreenCard";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import { BodyMediumText, TitleMediumText } from "../../components/FormattedText";
import Callout from "../../ui_atoms/Callout";
import Label from "../../ui_atoms/Label";
import ListItem from "../../ui_atoms/ListItem";
import VStack from "../../ui_atoms/VStack";
import Text from "../../components/Text";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import { SIGN_PENDING_CONTRACTS } from "../../graphql/signature";
import { missionsSigningLaunched } from "../../atoms/missions";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { ProfileType, SimpleSigningState } from "../../__generated__/graphql";
import { CoreMissionFieldsFragment } from "../../__generated__/operations/graphql";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";

// The mutation only schedules the signatures server-side and returns a count, so the progress can
// only be observed by re-reading the contracts until they stop awaiting the current user.
const POLL_INTERVAL_MS = 2000;
const COLLAPSED_ROW_COUNT = 3;

const RING_SIZE = 180;
const RING_STROKE = 14;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

type ProgressRingProps = { signedCount: number; total: number; isDone: boolean };

const ProgressRing: React.FC<ProgressRingProps> = ({ signedCount, total, isDone }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const progress = total > 0 ? signedCount / total : 0;

    return (
        <View style={styles.ring}>
            {/* Drawn from 12 o'clock: the arc is rotated a quarter turn counter-clockwise. */}
            <Svg width={RING_SIZE} height={RING_SIZE}>
                <Circle
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    stroke={colors.lighterGrey}
                    strokeWidth={RING_STROKE}
                    fill="none"
                />
                <Circle
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    stroke={colors.secondary}
                    strokeWidth={RING_STROKE}
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
                    transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                />
            </Svg>
            <View style={styles.ringContent}>
                {isDone ? (
                    <Icon name="check-heavy" size={RING_SIZE * 0.65} color={colors.secondary} />
                ) : (
                    <>
                        <Text style={styles.ringCount}>{signedCount}</Text>
                        <BodyMediumText style={styles.ringTotal}>
                            {i18n.t("screens.user.missions.multi_sign_progress_count", { total })}
                        </BodyMediumText>
                    </>
                )}
            </View>
        </View>
    );
};

type MissionsMultiSignProgressScreenProps = StackScreenProps<
    UserMissionsTabParamList,
    "MissionsMultiSignProgress"
>;

const MissionsMultiSignProgressScreen: React.FC<MissionsMultiSignProgressScreenProps> = ({
    route,
    navigation,
}) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const [, setSigningLaunched] = useAtom(missionsSigningLaunched);
    const [isScheduled, setIsScheduled] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const { missionIds } = route.params;

    const [signPendingContracts, { error: signError }] = useMutation(SIGN_PENDING_CONTRACTS);

    const { data, startPolling, stopPolling } = useQuery(FIND_MY_MISSIONS, { skip: !profileType });

    const missions = useMemo(
        () => (data?.findMyMissions?.elements ?? []).filter((m) => missionIds.includes(m.id)),
        [data?.findMyMissions?.elements, missionIds],
    );

    // "Signed" means the contract no longer waits on the current user — the outcome of the action
    // just launched. Waiting for the counterparty instead would leave the counter stuck for days.
    const awaitingSelfState =
        profileType === ProfileType.Client
            ? SimpleSigningState.WaitingClient
            : SimpleSigningState.WaitingWorker;

    const isMissionSigned = useCallback(
        (mission: CoreMissionFieldsFragment) =>
            mission.currentContract?.signingState !== awaitingSelfState,
        [awaitingSelfState],
    );

    const signedCount = useMemo(
        () => missions.filter(isMissionSigned).length,
        [missions, isMissionSigned],
    );

    const isDone = missions.length > 0 && signedCount === missions.length;

    // Nothing to watch before the signatures are scheduled, and nothing left to watch once every
    // contract has moved on.
    useEffect(() => {
        if (isScheduled && !isDone) {
            startPolling(POLL_INTERVAL_MS);
        } else {
            stopPolling();
        }
    }, [isScheduled, isDone, startPolling, stopPolling]);

    useEffect(() => {
        if (isScheduled) {
            return;
        }
        const contractIds = missions
            .map((mission) => mission.currentContract?.id)
            .filter((id): id is string => !!id);

        if (contractIds.length === 0) {
            return;
        }

        setIsScheduled(true);
        // The list behind must leave its selection mode: those contracts are on their way.
        setSigningLaunched(true);
        void signPendingContracts({ variables: { contractIds } }).catch(() => {
            // Surfaced through `signError` below.
        });
    }, [isScheduled, missions, signPendingContracts, setSigningLaunched]);

    const onClose = useCallback(() => {
        navigation.popTo("MissionList");
    }, [navigation]);

    const visibleMissions = useMemo(
        () => (isExpanded ? missions : missions.slice(0, COLLAPSED_ROW_COUNT)),
        [isExpanded, missions],
    );

    const closeButton = useMemo(
        () => (
            <Button
                key="close"
                size="large"
                style={styles.closeButton}
                label={i18n.t(
                    isDone ? "screens.user.missions.multi_sign_progress_done_close" : "close",
                )}
                onPress={onClose}
            />
        ),
        [i18n, isDone, onClose, styles.closeButton],
    );

    return (
        <ScreenCard
            buttons={[closeButton]}
            headband={
                // Only a failed mutation is worth reporting: it means nothing was signed. A failed
                // poll just delays the counter, and the next tick retries on its own — saying the
                // signature failed there would be plainly wrong.
                signError
                    ? buildErrorHeadband(i18n.t("screens.user.missions.multi_sign_progress_error"))
                    : undefined
            }
        >
            <VStack v="2/3" h="2/3" alignment="center">
                <ProgressRing signedCount={signedCount} total={missions.length} isDone={isDone} />
                <TitleMediumText style={styles.title}>
                    {i18n.t(
                        isDone
                            ? "screens.user.missions.multi_sign_progress_done_title"
                            : "screens.user.missions.multi_sign_progress_title",
                        { count: signedCount },
                    )}
                </TitleMediumText>
                <BodyMediumText style={styles.description}>
                    {i18n.t(
                        isDone
                            ? "screens.user.missions.multi_sign_progress_done_description"
                            : "screens.user.missions.multi_sign_progress_description",
                    )}
                </BodyMediumText>
            </VStack>

            <View style={styles.card}>
                {visibleMissions.map((mission, index) => {
                    const isClient = profileType === ProfileType.Client;
                    const isLast = index === visibleMissions.length - 1;
                    const signed = isMissionSigned(mission);

                    return (
                        <ListItem
                            key={mission.id}
                            inGroup={isLast && missions.length <= COLLAPSED_ROW_COUNT}
                            avatarPath={
                                isClient ? mission.workerInfo.avatarUrl : mission.clientInfo.logoUrl
                            }
                            title={isClient ? mission.workerInfo.name : mission.clientInfo.name}
                            badge={
                                signed ? (
                                    <Label
                                        mode="success"
                                        text={i18n.t(
                                            "screens.user.missions.multi_sign_progress_signed",
                                        )}
                                    />
                                ) : (
                                    <Loading size="small" />
                                )
                            }
                        />
                    );
                })}
                {!isExpanded && missions.length > COLLAPSED_ROW_COUNT ? (
                    <ListItem
                        inGroup
                        labels={[
                            {
                                mode: "miniCtaBold",
                                text: i18n.t("screens.user.missions.multi_sign_progress_see_more"),
                                onPress: () => setIsExpanded(true),
                            },
                        ]}
                        onPress={() => setIsExpanded(true)}
                        rightActionMode="none"
                    />
                ) : null}
            </View>

            {/* Only while there is something left to wait for. */}
            {isDone ? null : (
                <VStack h="2/3" v="1/3">
                    <Callout
                        mode="info"
                        icon="clock"
                        markdown={i18n.t("screens.user.missions.multi_sign_progress_background")}
                    />
                </VStack>
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors, gridSize, marginSize, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                ring: {
                    alignItems: "center",
                    justifyContent: "center",
                    marginVertical: gridSize / 2,
                },
                ringContent: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: "center",
                    justifyContent: "center",
                },
                ringCount: {
                    ...fonts.titleLarge,
                    fontFamily: "Inter-Bold",
                    fontSize: 56,
                    lineHeight: 64,
                },
                ringTotal: {
                    color: colors.greyAccessible,
                },
                title: {
                    textAlign: "center",
                    marginTop: gridSize / 2,
                },
                description: {
                    textAlign: "center",
                    marginTop: gridSize / 3,
                },
                card: {
                    marginHorizontal: marginSize,
                    marginTop: gridSize,
                    borderWidth: 1,
                    borderColor: colors.lighterGrey,
                    borderRadius: gridSize / 2,
                    overflow: "hidden",
                },
                closeButton: {
                    alignSelf: "stretch",
                },
            }),
        [colors.lighterGrey, colors.greyAccessible, fonts.titleLarge, gridSize, marginSize],
    );
};

export default MissionsMultiSignProgressScreen;
